import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { dataSelector } from '@nextiva/global-store';
import {
  Grid,
  Box,
  Icon,
  Icons,
  Typography,
} from '@nextiva/next-ui-components';
import { Page } from 'containers';
import { SvgOpenClose } from 'components/SvgOpenClose';
import { useGetConsentGrantStatus } from 'hooks/useGetConsentGrantStatus/useGetConsentGrantStatus';
import { useSaveConsentGrantStatus } from 'hooks/useSaveConsentGrantStatus/useSaveConsentGrantStatus';
import { GlobalStoreInterface } from 'interface/globalStore';
import {
  AuthorizeButton,
  AuthorizedButton,
  GoToFive9Button,
  AccordionHeader,
  AccordionSummary,
  StyledDivider,
  AuthorizedButtonContainer,
} from './ContactCenter.styles';

const base64 = require('base-64');

const ContactCenter = () => {
  const [accordionToggle, setAccordionToggle] = useState(true);
  const globalStoreData: GlobalStoreInterface | undefined = useSelector(
    dataSelector,
  );

  const { data: consentGrantStatus } = useGetConsentGrantStatus();
  const consentGrantStatusMutation = useSaveConsentGrantStatus();
  const isAccountAuthorized =
    consentGrantStatus?.data?.grantStatus === 'GRANTED';
  const hipaaQueryParameter = globalStoreData?.user?.accountInfo?.hipaa
    ? 'accountType=H'
    : 'accountType=R';

  const handleAccordion = () => {
    setAccordionToggle(!accordionToggle);
  };

  const handleAuthorize = () => {
    consentGrantStatusMutation.mutate();
  };

  const goFive9Website = () => {
    const corpAccId = globalStoreData?.user?.accountInfo?.corpAcctNbr;
    const encodedAccId: string = base64.encode(corpAccId);
    /* Redirects to five9 link in new tab */
    const five9Url = `https://cdn.prod.us.five9.com/uc/globalauthflow/stable/index.html?vendor=nextiva&accountId=${encodedAccId}&${hipaaQueryParameter}`;
    window.open(five9Url, '_blank');
  };

  return (
    <Page className="contactCenterPage" title="Contact Center">
      <Box>
        <AccordionHeader
          data-testid="PlatformAdminUI-Messaging-Accordion-Collapse-Button"
          onClick={handleAccordion}
        >
          <Grid container>
            <Box>
              <Typography className="five9Header">
                Five9 <Icon icon={Icons.helpSolid} />
              </Typography>
              <Typography className="five9SubHeader">
                Enable five9 directory monitoring and share agent presence
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box className="toggleIcon">
              <SvgOpenClose toggle={accordionToggle} />
            </Box>
          </Grid>
        </AccordionHeader>
        {accordionToggle && (
          <AccordionSummary>
            <Typography className="five9ListHeading">
              Five9 will require access to:
            </Typography>
            <Typography className="five9List">
              <ul>
                <li>View your user account information</li>
                <li>View directory information</li>
                <li>View your presence</li>
              </ul>
            </Typography>
            {!isAccountAuthorized && (
              <Typography className="five9Text">
                Click “Authorize” to enable directory and presence integration
                between Five9 and Nextiva.
              </Typography>
            )}

            <Grid container justifyContent="end">
              {isAccountAuthorized ? (
                <Grid item>
                  <Grid container>
                    <AuthorizedButtonContainer>
                      <GoToFive9Button
                        color="primary"
                        data-testid="PlatformAdminUI-Messaging-Visit-Five9-Website-Button"
                        size="small"
                        variant="outlined"
                        onClick={goFive9Website}
                      >
                        Visit Five9 website
                      </GoToFive9Button>
                      <AuthorizedButton
                        data-testid="PlatformAdminUI-Messaging-AuthorizedText"
                        onClick={handleAuthorize}
                      >
                        <Icon icon={Icons.checkCircleSolid} /> Authorized
                      </AuthorizedButton>
                    </AuthorizedButtonContainer>
                  </Grid>
                </Grid>
              ) : (
                <Grid item>
                  <AuthorizeButton
                    color="primary"
                    size="small"
                    variant="contained"
                    data-testid="PlatformAdminUI-Messaging-AuthorizeButtonText"
                    onClick={handleAuthorize}
                  >
                    Authorize
                  </AuthorizeButton>
                </Grid>
              )}
            </Grid>
          </AccordionSummary>
        )}
      </Box>
      <StyledDivider />
    </Page>
  );
};

export default ContactCenter;
